import { IonIcon, IonLabel, IonText } from "@ionic/react";
import { closeOutline } from "ionicons/icons";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import CalendarIcon from "@/assets/icons/calendar.svg";
import CaretDownRedIcon from "@/assets/icons/caret-down-red.svg";
import DotsMatrixIcon from "@/assets/icons/dots-matrix.svg";
import Button from "@/components/common/Button";
import DateRangeErrorMessage from "@/components/operations/DateRangeErrorMessage/DateRangeErrorMessage";
import SearchDatePicker, {
  SearchDatePickerRef,
} from "@/components/operations/SearchDatePicker/SearchDatePicker";
import { ModalProps } from "@/hooks/useModal";
import { typesFilters } from "@/interfaces/FilterCalendarInterface/FilterCalendarInterface";
import {
  InitialStatus,
  Status,
} from "@/interfaces/OperationsDashboardInterface/OperationsDashboardInterface";
import {
  saveCartId,
  saveFilters,
  saveProposalRef,
} from "@/redux/filters/filters";
import { RootState } from "@/store";
import {
  AvailableSpecificFilters,
  getDateRangeError,
  getMinAllowedDate,
  MAX_DATE_RANGE_DAYS,
} from "@/utils/dashboardDateRange";

import styles from "./OperationsFiltersModal.module.scss";

export interface OperationsFiltersModalProps extends ModalProps {
  onDismiss: () => void;
  dateStart: (date: string) => void;
  dateEnd: (date: string) => void;
  proposalReference: (newSearch: string) => void;
  cartID: (newSearch: string) => void;
  updateFilterValues: (newValues: Partial<typesFilters>) => void;
  updateCurrentButtons: (currentButtons: Status[]) => void;
  currentButtons: Status[];
  // Indica si hay un filtro por nombre/DNI, ID de carrito o código AG aplicado
  specificFilter: boolean;
  availableSpecificFilters: AvailableSpecificFilters;
}

const DATE_RANGE_ERROR_ID = "operations-filters-date-range-error";

export default function OperationsFiltersModal({
  onDismiss,
  dateStart,
  dateEnd,
  proposalReference,
  cartID,
  currentButtons,
  updateFilterValues,
  updateCurrentButtons,
  specificFilter,
  availableSpecificFilters,
}: OperationsFiltersModalProps) {
  const dispatch = useDispatch();
  const startDateFilter = useSelector(
    (state: RootState) => state.filters.startDate,
  );
  const endDateFilter = useSelector(
    (state: RootState) => state.filters.endDate,
  );
  const proposalRefFilter = useSelector(
    (state: RootState) => state.filters.proposalReference,
  );
  const cartFilter = useSelector((state: RootState) => state.filters.cartId);

  const [startDateInput, setStartDateInput] = useState<string | null | false>(
    startDateFilter,
  );
  const [endDateInput, setEndDateInput] = useState<string | null | false>(
    endDateFilter,
  );
  const [proposalRefInput, setProposalRefInput] = useState<string | null>(
    proposalRefFilter,
  );
  const [cartIdInput, setCartIdInput] = useState<string | null>(cartFilter);
  const [buttons, setButtons] = useState<Status[]>(currentButtons);
  const [filtersSelected, setFiltersSelected] =
    useState<Partial<typesFilters>>();
  const datePickerRef = useRef<SearchDatePickerRef>(null);

  const dateRangeError = getDateRangeError(
    { fromDate: startDateInput || "", toDate: endDateInput || "" },
    specificFilter,
  );

  useEffect(() => {
    const mql = window.matchMedia("(orientation: portrait)");
    const handleOrientationChange = () => onDismiss();
    mql.addEventListener("change", handleOrientationChange);
    return () => mql.removeEventListener("change", handleOrientationChange);
  }, [onDismiss]);

  const handleApliedFilter = () => {
    if (dateRangeError) {
      return;
    }
    startDateInput && dateStart(startDateInput);
    endDateInput && dateEnd(endDateInput);
    proposalRefInput && proposalRefInput.length > 3
      ? proposalReference(proposalRefInput)
      : proposalReference("");
    cartIdInput ? cartID(cartIdInput) : cartID("");
    updateFilterValues(filtersSelected!);
    updateCurrentButtons(buttons);
    dispatch(saveFilters(buttons));
    dispatch(saveCartId(cartIdInput || ""));
    dispatch(saveProposalRef(proposalRefInput || ""));
    onDismiss();
  };

  const updateButtonsWithSelectedStatus = (
    buttons: Status[],
    initials: InitialStatus,
    isSelected: boolean,
  ): Status[] =>
    buttons.map((button) => ({
      ...button,
      isSelected:
        button.initials === initials
          ? isSelected
          : button.initials === InitialStatus.Todas
            ? false
            : button.isSelected,
    }));

  const selectButtons = async (
    initials: InitialStatus,
    isSelected: boolean,
  ) => {
    let updatedButtons: Status[] = [];
    if (initials === InitialStatus.Todas) {
      updatedButtons = buttons.map((button) => ({
        ...button,
        isSelected: button.initials === initials,
      }));
    } else {
      updatedButtons = updateButtonsWithSelectedStatus(
        buttons,
        initials,
        isSelected,
      );
      updatedButtons
        .filter((button) => button.isSelected)
        .map((button) => button.initials);
    }
    setButtons(updatedButtons);

    const selectedButtonValues = updatedButtons
      .filter((button) => button.isSelected)
      .map((button) => {
        if (button.initials === InitialStatus.Todas) {
          return null;
        }
        return `&business_status=${button.initials}`;
      })
      .filter((value) => value !== null)
      .join("");

    setFiltersSelected({ buttons: selectedButtonValues, offset: 0 });
    currentButtons = updatedButtons;
  };

  const openDatePicker = () => {
    datePickerRef.current?.openDatePicker();
  };

  return (
    <div id="bottom-modal-operations-filters" className={styles.container}>
      <div className={styles.closeButton}>
        <IonIcon
          id="button-close-setup-biometry-modal"
          icon={closeOutline}
          onClick={() => onDismiss()}
        />
      </div>
      <div className={styles.infoContainer}>
        <div className={styles.filterCalendar}>
          <div className={styles.filterCalendarWrapper}>
            <div className={styles.filterCalendarHeader}>
              <IonLabel className={styles.title}>Filtrar por ...</IonLabel>
              <IonLabel className={styles.subtitle}>
                Selecciona una fecha
              </IonLabel>
            </div>

            <div
              className={`${styles.calendarFilterContainer} ${
                dateRangeError ? styles.invalid : ""
              }`}
              onClick={openDatePicker}
            >
              <div className={styles.calendarFilter}>
                <div className={styles.inputCalendar}>
                  <SearchDatePicker
                    ref={datePickerRef}
                    dateStart={(date) => {
                      setStartDateInput(date);
                      dateStart(date);
                    }}
                    dateEnd={(date) => {
                      setEndDateInput(date);
                      dateEnd(date);
                    }}
                    savedStartDate={startDateInput || ""}
                    savedEndDate={endDateInput || ""}
                    clear
                    monthsShown={1}
                    minDate={getMinAllowedDate().toDate()}
                    maxRangeDays={
                      specificFilter ? undefined : MAX_DATE_RANGE_DAYS
                    }
                    errorId={dateRangeError ? DATE_RANGE_ERROR_ID : undefined}
                    availableSpecificFilters={availableSpecificFilters}
                  />
                </div>
                <IonIcon src={CalendarIcon} className="icon-search" />
              </div>
              <div className={styles.selectFilter}>
                <IonText>Selecciona</IonText>
                <IonIcon src={CaretDownRedIcon} className="icon-search" />
              </div>
            </div>
            <div aria-live="polite">
              {dateRangeError && (
                <IonText
                  id={DATE_RANGE_ERROR_ID}
                  role="alert"
                  className={styles.dateRangeError}
                >
                  <DateRangeErrorMessage
                    error={dateRangeError}
                    availableFilters={availableSpecificFilters}
                  />
                </IonText>
              )}
            </div>
            <div className={styles.status}>
              <div className={styles.statusTitle}>
                <IonLabel className={styles.statusTitleInfo}>Estado</IonLabel>
              </div>
              <div className={styles.statusSelecteds}>
                {buttons.map(({ name, initials, isSelected }: Status, idx) => (
                  <div
                    className={
                      isSelected
                        ? styles.buttonStyleSelected
                        : styles.buttonStyle
                    }
                    key={initials}
                    id={initials}
                    onClick={() => selectButtons(initials, !isSelected)}
                  >
                    {idx === 0 && (
                      <IonIcon
                        className={styles.matrixIcon}
                        src={DotsMatrixIcon}
                      />
                    )}
                    <IonText className={isSelected ? "selected-text" : ""}>
                      {name}
                    </IonText>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className={styles.buttons}>
            <Button
              id="button-setup-biometry-modal-confirm"
              onClick={handleApliedFilter}
              disabled={!!dateRangeError}
            >
              Aplicar filtros
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
